import {
  Backdrop,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import api from 'api/axios';
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MedicalFacultyModal from "./MedicalFacultyModal";
import { useTranslation } from "react-i18next";

const MedicalFacultyList = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [medicalFaculties, setMedicalFaculties] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [backDropOpen, setBackDropOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState({});
  const [isEditDoctor, setIsEditDoctor] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const prevSearchRef = useRef(search);

  useEffect(() => {
    if (prevSearchRef.current !== search) {
      prevSearchRef.current = search;
      if (pageNumber !== 0) {
        setPageNumber(0);
      } else {
        const delayDebounce = setTimeout(() => {
          getMedicalFaculties();
        }, 500);
        return () => clearTimeout(delayDebounce);
      }
      return;
    }
    const delayDebounce = setTimeout(() => {
      getMedicalFaculties();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [rowsPerPage, pageNumber, search]);

  const getMedicalFaculties = (e) => {
    try {
      setBackDropOpen(true);
      const params = { limit: rowsPerPage, page: pageNumber + 1 };
      if (search.trim()) {
        params.search = search.trim();
      }
      api
        .get("/hospital/medicalfFacultyList", { params })
        .then((response) => {
          const list = response?.data?.data?.medicalFacultyList || [];
          const total = response?.data?.data?.totalCount || 0;
          setMedicalFaculties(list);
          setCount(total);
          setBackDropOpen(false);
        })
        .catch(() => {
          setBackDropOpen(false);
          toast.error("Failed to fetch hospitals");
        });
    } catch (error) {
      setBackDropOpen(false);
      toast.error("Error fetching hospital list");
    }
  };

  const handleExport = async (format = "xlsx") => {
    if (exporting) return; // double click guard
    if (count === 0) {
      toast.warn("No data to export!");
      return;
    }
    try {
      setExporting(true);
      setBackDropOpen(true);
      const params = { format };
      if (search.trim()) params.search = search.trim();

      const response = await api.get("/hospital/export", {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `hospitals.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported successfully!");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
      setBackDropOpen(false);
    }
  };

  const handleActionClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    handleModal(true, selectedRow);
    handleActionClose();
  };

  const handleChangePage = (event, newPage) => setPageNumber(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  const handleModal = (isEdit, row = {}) => {
    setData(row);
    setIsEditDoctor(isEdit);
    setOpenModal(true);
  };

  const handleModalClose = (buttonText = "save") => {
    setData({});
    if (buttonText !== "close") getMedicalFaculties();
    setOpenModal(false);
  };

  return (
    <>
      <div style={{ padding: "24px", backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={backDropOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Top bar — search + button same row */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "12px 16px",
            borderRadius: "12px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ width: "300px" }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              placeholder="Search hospital, city or mobile..."
              variant="outlined"
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={() => handleExport("xlsx")}
              disabled={exporting}
              style={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: "13px",
                padding: "8px 16px",
                borderColor: exporting ? "#9ca3af" : "#16a34a",
                color: exporting ? "#9ca3af" : "#16a34a",
                whiteSpace: "nowrap",
              }}
            >
              {exporting ? "Exporting..." : "Export Excel"}
              </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("pdf")}
              disabled={exporting}
              style={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: "13px",
                padding: "8px 16px",
                borderColor: exporting ? "#9ca3af" : "#dc2626",
                color: exporting ? "#9ca3af" : "#dc2626",
                whiteSpace: "nowrap",
              }}
            >
              {exporting ? "Exporting..." : "Export PDF"}
            </Button>

            <Button
              variant="contained"
              onClick={() => handleModal(false)}
              style={{
                backgroundColor: "#1d4ed8",
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: "none",
                fontWeight: 500,
                fontSize: "13px",
                padding: "8px 20px",
                whiteSpace: "nowrap",
              }}
            >
              + {t("CREATE_MEDIAL_FAC")}
            </Button>
          </div>
        </div>

        {/* Table card */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
                  {["Hospital Name", "City", "State", "Pincode", "Created On", "Admin", "Action"].map((col, i) => (
                    <th
                      key={i}
                      style={{
                        padding: "12px 20px",
                        textAlign: i === 6 ? "right" : "left",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#1d4ed8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {medicalFaculties.map((row, index) => {
                  const isActive = row.hospitalAdmin?.status === "ACTIVE";
                  const name = row.hospitalAdmin?.name || "";
                  const initials = name
                    ? name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                    : "?";

                  return (
                    <tr
                      key={index}
                      style={{ borderBottom: "1px solid #f3f4f6", transition: "background 0.15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f7ff")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td style={{ padding: "14px 20px", fontWeight: 500, color: "#111827", textTransform: "uppercase" }}>
                      {row.name}
                    </td>
                      <td style={{ padding: "14px 20px", color: "#6b7280", textTransform: "uppercase" }}>
                        {row.address?.city}
                      </td>
                      <td style={{ padding: "14px 20px", color: "#6b7280", textTransform: "uppercase" }}>
                        {row.address?.state}
                      </td>
                      <td style={{ padding: "14px 20px", color: "#6b7280" }}>
                        {row.address?.pincode}
                      </td>
                      <td style={{ padding: "14px 20px", color: "#6b7280" }}>
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : "-"}
                      </td>

                      {/* Admin column — avatar + info side by side */}
                      <td style={{ padding: "14px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {/* Avatar circle */}
                          <div
                            style={{
                              width: "34px",
                              height: "34px",
                              minWidth: "34px",
                              borderRadius: "50%",
                              backgroundColor: "#dbeafe",
                              color: "#1d4ed8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {initials}
                          </div>

                          {/* Name, phone, badge — stacked vertically */}
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontWeight: 500, color: "#111827", fontSize: "13px" , textTransform: "uppercase" }}>
                              {name || "-"}
                            </span>
                            <span style={{ fontSize: "12px", color: "#6b7280" }}>
                              {row.hospitalAdmin?.phone || "-"}
                            </span>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "1px 8px",
                                fontSize: "10px",
                                fontWeight: 600,
                                borderRadius: "999px",
                                width: "fit-content",
                                backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
                                color: isActive ? "#15803d" : "#dc2626",
                              }}
                            >
                              {isActive ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td style={{ padding: "14px 20px", textAlign: "right" }}>
                        <IconButton size="small" onClick={(e) => handleActionClick(e, row)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {medicalFaculties.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: "14px" }}>
              No Hospitals Found
            </div>
          )}

          <div style={{ borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "flex-end", padding: "4px 8px" }}>
            <TablePagination
              component="div"
              count={count}
              page={pageNumber}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderBottom: "none" }}
            />
          </div>
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
        PaperProps={{
          style: {
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            minWidth: "120px",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem onClick={handleEdit} style={{ fontSize: "13px" }}>Edit</MenuItem>
      </Menu>

      {openModal && (
        <MedicalFacultyModal
          open={openModal}
          onClose={handleModalClose}
          medicalFaculties={data}
          isEditDoctor={isEditDoctor}
        />
      )}
    </>
  );
};

export default MedicalFacultyList;
